// src/components/Charts/line/AnalysisPageLineChart.jsx
import { useState, useEffect, useContext, useRef } from 'react';
import * as echarts from 'echarts';
import useAxios from '../../../hooks/useAxios';
import AuthContext from '../../../contexts/auth-context';
import AppContext from '../../../contexts/app-context';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { urls } from '../../config/variables';

// Static titles (no need for useMemo)
const TITLES = {
  costs: 'Approved vs Rejected costs',
  approvalRate: 'Approval rate',
  avgCost: 'Average cost',
  totalEncounters: 'Total encounters',
};

const formatAxisLabel = (isoDate, frequency) => {
  return new Date(isoDate).toLocaleDateString('en-US', frequency === 'yearly' ? {year: "numeric"} : frequency === 'monthly' ? {month: 'short'} : {month: 'short', day: 'numeric'})
};

const endpointForMetric = (metricKey) => {
  switch (metricKey) {
    case 'approvalRate':
      return urls.APPROVAL_RATE;
    case 'avgCost':
      return urls.AVERAGE_COST;
    case 'costs':
    case 'totalEncounters':
    default:
      return urls.ENCOUNTER_EXPENSES;
  }
};

const AnalysisPageLineChart = ({hideMenu}) => {
  const [period, setPeriod] = useState('weekly');


  // UI: metric selection
  const [metric, setMetric] = useState('costs');
  const [metricAnchorEl, setMetricAnchorEl] = useState(null);
  const openMetricMenu = (event) => setMetricAnchorEl(event.currentTarget);
  const closeMetricMenu = () => setMetricAnchorEl(null);

  const { operation } = useAxios();

  // Auth + global filter
  const { token = '' } = useContext(AuthContext) || {};
  const { globalFilterQuery } = useContext(AppContext);

  // ECharts instance refs
  const chartInstanceRef = useRef(null);
  const chartElementRef = useRef(null);

  // Request management refs
  const latestRequestIdRef = useRef(0);
  const abortControllerRef = useRef(null);

  // Hide tooltip safely (avoid errors if chart is disposing)
  const hideTooltip = () => {
    const chart = chartInstanceRef.current;
    try {
      if (chart && !chart.isDisposed?.()) {
        chart.dispatchAction({ type: 'hideTip' });
      }
    } catch {
      // ignore
    }
  };

  const selectMetric = (newMetric) => {
    hideTooltip();
    setMetric(newMetric);
    closeMetricMenu();
  };

  // Initialize chart instance once with a throttled ResizeObserver
  useEffect(() => {
    if (!chartElementRef.current || chartInstanceRef.current) return;

    chartInstanceRef.current = echarts.init(chartElementRef.current);

    const resizeObserver = new ResizeObserver(() => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.resize();
      }
    });

    resizeObserver.observe(chartElementRef.current);

    return () => {
      resizeObserver.disconnect();

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);


  // Fetch + render when metric or filters change
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (!token || !chart) return;

    // Abort any in-flight request before starting a new one
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const requestId = ++latestRequestIdRef.current;

    // Determine frequency and construct URL with default
    const queryParams = new URLSearchParams(globalFilterQuery);
    if (!queryParams.has('frequency_date')) {
      queryParams.set('frequency_date', period);
    }
    const requestUrl = `${endpointForMetric(metric)}?${queryParams.toString()}`;

    // Show loading overlay
    chart.showLoading('default', {
      text: '',
      color: '#9aa4b2',
      maskColor: 'rgba(255,255,255,0.35)',
    });

    // Promise wrapper for your operation helper (so we can await)
    const doRequest = () =>
      new Promise((resolve, reject) => {
        operation(
          {
            method: 'GET',
            url: requestUrl,
            headers: { Authorization: `token ${token}` },
            signal: abortController.signal,
          },
          (rows) => resolve(rows),
          (error) => reject(error)
        );
      });

    // formatters for y-axis/tooltip per metric
    const yFormatterFor = (type) =>
      type === 'approvalRate'
        ? (val) => `%${val}`
        : type === 'avgCost' || type === 'costs'
        ? (val) => `$${val}`
        : (val) => `${val}`;

    // ECharts tooltip config with de-dup on series
    const makeTooltip = (type) => ({
      trigger: 'axis',
      confine: true,
      transitionDuration: 0,
      hideDelay: 0,
      formatter: (params) => {
        const seenSeries = new Set();
        const lines = [];
        const header = params?.[0]?.axisValueLabel ?? '';
        if (header) lines.push(header);
        const formatValue = yFormatterFor(type);

        for (const point of params) {
          const key = point.seriesId ?? point.seriesIndex;
          if (seenSeries.has(key)) continue;
          seenSeries.add(key);

          const pointValue = Array.isArray(point.data) ? point.data[1] : point.data;
          lines.push(`${point.marker} ${point.seriesName}: ${formatValue(pointValue)}`);
        }
        return lines.join('<br/>');
      },
    });

    // Temporarily disable tooltip during heavy updates to avoid flicker
    const disableTooltip = () => {
      hideTooltip();
      chart.setOption(
        { tooltip: { triggerOn: 'none' }, axisPointer: { show: false } },
        { notMerge: false, silent: true }
      );
    };
    const enableTooltip = () => {
      requestAnimationFrame(() => {
        chart.setOption(
          { tooltip: { triggerOn: 'mousemove|click' }, axisPointer: { show: true } },
          { notMerge: false, silent: true }
        );
      });
    };

    (async () => {
      try {
        const responseRows = await doRequest();
        // If aborted or a newer request started, ignore this response
        if (abortController.signal.aborted || requestId !== latestRequestIdRef.current) return;

        // Sort by date ascending
        const sortedRows = [...(responseRows || [])].sort(
          (a, b) => new Date(a.f_date) - new Date(b.f_date)
        );

        // Build categories for X axis
        const categories = sortedRows.map((row) => formatAxisLabel(row.f_date, period));

        let option;

        if (metric === 'costs') {
          const approvedCosts = sortedRows.map((row) =>
            row.accepted_cost == null ? 0 : Number(row.accepted_cost)
          );
          const rejectedCosts = sortedRows.map((row) =>
            row.rejected_cost == null ? 0 : Number(row.rejected_cost)
          );

          option = {
            animation: true,
            animationDuration: 250,
            animationDurationUpdate: 250,
            animationEasing: 'cubicOut',
            animationEasingUpdate: 'cubicOut',
            tooltip: makeTooltip('costs'),
            legend: {
              data: ['Approved cost', 'Rejected cost'],
              top: '10px',
              right: '160px',
              icon: 'circle',
              itemGap: 30,
              textStyle: { fontFamily: 'Outfit' },
            },
            grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true },
            xAxis: {
              type: 'category',
              boundaryGap: false,
              data: categories,
              axisLabel: { hideOverlap: true },
            },
            yAxis: {
              type: 'value',
              axisLabel: { formatter: yFormatterFor('costs') },
            },
            series: [
              {
                id: 'series-rejected',
                name: 'Rejected cost',
                type: 'line',
                showSymbol: true,
                data: rejectedCosts,
                itemStyle: { color: '#F44336' },
              },
              {
                id: 'series-approved',
                name: 'Approved cost',
                type: 'line',
                showSymbol: true,
                data: approvedCosts,
                itemStyle: { color: '#4CAF50' },
              },
            ],
          };
        } else if (metric === 'totalEncounters') {
          const encounterCounts = sortedRows.map((row) => Number(row.count ?? 0));
          option = {
            animation: true,
            animationDuration: 250,
            animationDurationUpdate: 250,
            animationEasing: 'cubicOut',
            animationEasingUpdate: 'cubicOut',
            tooltip: makeTooltip('totalEncounters'),
            legend: {
              data: [TITLES.totalEncounters],
              top: '10px',
              right: '160px',
              icon: 'circle',
              itemGap: 30,
              textStyle: { fontFamily: 'Outfit' },
            },
            grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true },
            xAxis: {
              type: 'category',
              boundaryGap: false,
              data: categories,
              axisLabel: { hideOverlap: true },
            },
            yAxis: {
              type: 'value',
              axisLabel: { formatter: yFormatterFor('totalEncounters') },
            },
            series: [
              {
                id: 'series-single',
                name: TITLES.totalEncounters,
                type: 'line',
                showSymbol: true,
                data: encounterCounts,
                itemStyle: { color: '#009688' },
              },
            ],
          };
        } else if (metric === 'approvalRate') {
          const approvalPercentages = sortedRows.map((row) =>
            row.approval_rate == null ? 0 : Math.round(Number(row.approval_rate) * 100)
          );
          option = {
            animation: true,
            animationDuration: 250,
            animationDurationUpdate: 250,
            animationEasing: 'cubicOut',
            animationEasingUpdate: 'cubicOut',
            tooltip: makeTooltip('approvalRate'),
            legend: {
              data: [TITLES.approvalRate],
              top: '10px',
              right: '160px',
              icon: 'circle',
              itemGap: 30,
              textStyle: { fontFamily: 'Outfit' },
            },
            grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true },
            xAxis: {
              type: 'category',
              boundaryGap: false,
              data: categories,
              axisLabel: { hideOverlap: true },
            },
            yAxis: {
              type: 'value',
              max: 100,
              axisLabel: { formatter: yFormatterFor('approvalRate') },
            },
            series: [
              {
                id: 'series-single',
                name: 'Approval rate',
                type: 'line',
                showSymbol: true,
                smooth: false,
                data: approvalPercentages,
                itemStyle: { color: '#6E8CFB' },
              },
            ],
          };
        } else {
          // avgCost
          const averageCosts = sortedRows.map((row) =>
            row.avg_cost == null ? 0 : Number(row.avg_cost)
          );

          option = {
            animation: true,
            animationDuration: 250,
            animationDurationUpdate: 250,
            animationEasing: 'cubicOut',
            animationEasingUpdate: 'cubicOut',
            tooltip: makeTooltip('avgCost'),
            legend: {
              data: [TITLES.avgCost],
              top: '10px',
              right: '160px',
              icon: 'circle',
              itemGap: 30,
              textStyle: { fontFamily: 'Outfit' },
            },
            grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true },
            xAxis: {
              type: 'category',
              boundaryGap: false,
              data: categories,
              axisLabel: { hideOverlap: true },
            },
            yAxis: {
              type: 'value',
              axisLabel: { formatter: yFormatterFor('avgCost') },
            },
            series: [
              {
                id: 'series-single',
                name: 'Average cost',
                type: 'line',
                showSymbol: true,
                smooth: false,
                data: averageCosts,
                itemStyle: { color: '#2196F3' },
              },
            ],
          };
        }

        // Disable tooltip while updating to avoid flicker
        disableTooltip();

        // Apply new options (including axes so scales refresh)
        chart.setOption(option, {
          notMerge: false,
          lazyUpdate: true,
          replaceMerge: ['xAxis', 'yAxis', 'series', 'legend'],
          silent: true,
        });

        enableTooltip();
      } catch (error) {
        if (!abortController.signal.aborted) {
          // eslint-disable-next-line no-console
          console.error('Chart data fetch error:', error);
        }
      } finally {
        if (!abortController.signal.aborted && requestId === latestRequestIdRef.current) {
          chart.hideLoading();
        }
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [metric, globalFilterQuery, token, operation, period]); // include operation now that it's stable via useCallback

  const handleChangePeriod = (event) => {
        setPeriod(event.target.value);
    };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Title dropdown */}
      {!hideMenu &&  <Box sx={{ position: 'absolute', top: -10, left: 10, zIndex: 1 }}>
        <Button
          variant="contained"
          onClick={openMetricMenu}
          endIcon={<KeyboardArrowDownRoundedIcon />}
          sx={{
            textTransform: 'none',
            color: 'text.primary',
            backgroundColor: '#fff',
            border: '1px solid #EBEEF5',
            borderRadius: '10px',
            boxShadow: '0 2px 4px rgba(15, 23, 42, 0.06)',
            px: 1.5,
            py: 0.75,
            '&:hover': { backgroundColor: '#fff' },
          }}
        >
          {TITLES[metric]}
        </Button>
        <Menu
          anchorEl={metricAnchorEl}
          open={Boolean(metricAnchorEl)}
          onClose={closeMetricMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MenuItem onClick={() => selectMetric('costs')}>{TITLES.costs}</MenuItem>
          <MenuItem onClick={() => selectMetric('approvalRate')}>{TITLES.approvalRate}</MenuItem>
          <MenuItem onClick={() => selectMetric('avgCost')}>{TITLES.avgCost}</MenuItem>
          <MenuItem onClick={() => selectMetric('totalEncounters')}>{TITLES.totalEncounters}</MenuItem>
        </Menu>
      </Box>}

      <Box sx={{ position: 'absolute', top: '8px', right: '50px', width: '80px', height: '20px', zIndex: 1 }}>
          <select
            value={period}
            onChange={handleChangePeriod}
            style={{
              padding: '2px 12px',
              borderRadius: '6px',
              border: '2px solid #7C8DB51F',
              backgroundColor: '#fff',
              fontSize: '14px',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              outline: 'none',
              cursor: 'pointer',
              appearance: 'auto',
            }}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </Box>

      {/* Chart container */}
      <Box ref={chartElementRef} sx={{ height: '300px' }} />
    </Box>
  );
};

export default AnalysisPageLineChart;